<template>
    <div>
        <div class="d-flex flex-wrap">
            <div class="flex-left-parent employee-info-header mb-2">
                <div>{{ $t('Language code')}}</div>
                <select v-model="$i18n.locale" @change="set_language($event.target.value)">
                    <option v-for="locale in $i18n.availableLocales" :key="locale">{{ locale }}</option>
                </select>

                <div>{{ $t('Year')}}</div>
                <input type="number" min="1900" max="2099" step="1" v-model="year" />
            </div>
            <div class="flex-right-parent ml-auto">
                <button class="btn btn-sm btn-primary" @click="test()">Test <i class="fas fa-spinner icn-spinner" v-show="testing"></i></button>
            </div>
        </div>
        <div class="d-flex flex-wrap text-sm text-center salary-report-header mb-4">
            <div class="card mr-md-4">
                <div
                    class="card-header text-white bg-primary"
                >{{ $t('Hours according to collective agreement') }}</div>
                <div class="card-body">
                    <table>
                        <tr v-for="(month, month_index) in 12" :key="month_index">
                            <td>{{ get_month(month_index) }}</td>
                            <td>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    :value="hours_agreement[month_index]"
                                    @change="update_hours_agreement(month_index, $event.target.value)"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>{{ $t('Total hours')}}</td>
                            <td>{{ sum_hours_agreement() }}</td>
                        </tr>
                    </table>
                </div>
            </div>

            <div class="card mr-md-4">
                <div class="card-header text-white bg-primary">Info</div>
                <div class="card-body">
                    <table>
                        <tr>
                            <td>{{ $t('Hours of previous year')}}</td>
                            <td class="text-left">
                                <input type="number" step="0.01" min="0" v-model="hours_prev" />
                            </td>
                        </tr>
                        <tr>
                            <td>{{ $t('Vacation of previous year')}}</td>
                            <td class="text-left">
                                <input type="number" step="1" v-model="holiday_hours_prev" />
                            </td>
                        </tr>
                        <tr>
                            <td>{{ $t('Hours according to CCT per day')}}</td>
                            <td class="text-left">
                                <input type="number" step="any" min="0" max="24" v-model="hours" />
                            </td>
                        </tr>
                        <tr>
                            <td>{{ $t('Holiday rights')}}</td>
                            <td class="text-left">
                                <input type="number" step="0.1" min="0" max="24" v-model="holidays" />
                                <span
                                    class="pl-2"
                                    style="width:40px;"
                                >{{ round_decimal(holidays * hours, 10000) }}</span>
                            </td>
                        </tr>
                    </table>
                </div>
            </div>

            <div class="card mr-md-4">
                <div class="card-header text-white bg-primary">Results</div>
                <div class="card-body">
                    <table>
                        <tr>
                            <td>{{ $t('Number of working hours')}}</td>
                            <td class="working">{{ cum_working_hours_month(11) }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('Number of absent hours')}}</td>
                            <td class="absence">{{ cum_absence_hours_month(11)}}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('Number of holiday hours')}}</td>
                            <td class="holiday">{{ cum_holiday_hours_month(11) }}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('Total meals')}}</td>
                            <td>{{ cum_meals(11)}}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('Total km')}}</td>
                            <td>{{ cum_km(11)}}</td>
                        </tr>
                        <tr>
                            <td>{{ $t('Cumulative hours balances')}}</td>
                            <td>{{ cum_hours_month(11) }}</td>
                        </tr>
                        <tr>
                            <td>{{$t('Cumulative hours of CCT')}}</td>
                            <td class="agreement">{{ cum_hours_agreement(11) }}</td>
                        </tr>
                        <tr>
                            <td>{{$t('Annual hours balances')}}</td>
                            <td>
                                <span
                                    :class="{'text-danger': balance_cum_hours_month(11)<0 }"
                                >{{ balance_cum_hours_month(11)}}</span>
                            </td>
                        </tr>
                        <tr>
                            <td>{{$t('Vacation balances')}}</td>
                            <td class="holiday">{{ balance_holiday_month(11)}}</td>
                        </tr>
                    </table>
                </div>
            </div>
        </div>

        <table class="table table-sm salary-report float-thead mb-4">
            <thead>
                <tr>
                    <th></th>
                    <th></th>
                    <th v-for="index in 31" :key="index">{{ index }}</th>
                    <th>{{ $t('Sum')}}</th>
                    <th>{{ $t('Statistics')}}</th>
                </tr>
            </thead>
            <tbody
                v-for="(month, month_index) in 12"
                :key="month_index"
                :set="daysInMonth = get_days_in_month(year, month_index)"
            >
                <tr>
                    <th class="text-left" rowspan="6">{{ get_month(month_index)}}</th>
                    <td class="text-left">{{ $t('Weekday')}}</td>
                    <td
                        v-for="(day, day_index) in daysInMonth"
                        :key="day_index"
                    >{{ get_weekday(year, month_index, day_index)}}</td>
                    <td v-for="empty in (31-daysInMonth)" :key="'empty' + empty" class="cell"></td>
                    <td></td>
                    <td rowspan="6">
                        <div class="label">{{ $t('Hours agreement')}} </div> <span class="agreement">{{ hours_agreement[month_index]}}</span>
                        <br />
                        <div class="label">{{ $t('Hour balances')}}</div>
                        <span
                            :class="{'text-danger': balance_hours(month_index)<0 }"
                        >{{ balance_hours(month_index) }}</span>
                        <br />
                        <div class="label">{{ $t('Cumulative hours balances')}}</div> {{ cum_hours_month(month_index) }}
                        <br />
                        <div class="label">{{$t('Cumulative hours of CCT')}} </div> <span class="agreement">{{ cum_hours_agreement(month_index) }}</span>
                        <br />
                        <div class="label">{{$t('Annual hours balances')}} </div>
                        <span
                            :class="{'text-danger': balance_cum_hours_month(month_index)<0 }"
                        >{{ balance_cum_hours_month(month_index)}}</span>
                        <br />
                        {{$t('Vacation balances')}}: <span class="holiday">{{ balance_holiday_month(month_index)}}</span>
                    </td>
                </tr>
                <tr>
                    <td class="text-left">{{ $t('Working hours')}}</td>
                    <td v-for="(day, day_index) in daysInMonth" :key="day_index" class="cell">
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="24"
                            :value="get_working_record(month_index, day_index)"
                            @change="update_working_record(month_index, day_index, $event.target.value)"
                        />
                    </td>
                    <td v-for="empty in (31-daysInMonth)" :key="'empty' + empty" class="cell"></td>
                    <td class="working">{{ sum_working_month(month_index) }}</td>
                </tr>
                <tr>
                    <td class="text-left">{{ $t('Absence code')}}</td>
                    <td v-for="(day, day_index) in daysInMonth" :key="day_index" class="cell">
                        <select
                            :value="get_absence_record(month_index, day_index)"
                            @change="update_absence_record(month_index, day_index, $event.target.value)"
                        >
                            <option></option>
                            <option
                                v-for="absence in absences"
                                :value="absence.id"
                                :key="'month' + month_index + 'day' + day_index + 'absence' + absence.id"
                            >{{ absence.code }}</option>
                        </select>
                    </td>
                    <td v-for="empty in (31-daysInMonth)" :key="'empty' + empty" class="cell"></td>
                    <td>
                        <span class="absence mr-2">{{ sum_absence_month(month_index) }}</span>
                        <span
                            class="holiday"
                            :title="$t('Vacation')"
                        >{{ sum_holiday_month(month_index) }}</span>
                    </td>
                </tr>
                <tr>
                    <td class="text-left">{{ $t('Hours RWH')}}</td>
                    <td v-for="(day, day_index) in daysInMonth" :key="day_index"></td>
                    <td v-for="empty in (31-daysInMonth)" :key="'empty' + empty" class="cell"></td>
                    <td></td>
                </tr>
                <tr>
                    <td class="text-left">{{ $t('Meal')}}</td>
                    <td v-for="(day, day_index) in daysInMonth" :key="day_index" class="cell">
                        <input
                            type="number"
                            step="1"
                            min="0"
                            max="1000"
                            :value="get_meal_record(month_index, day_index)"
                            @change="update_meal_record(month_index, day_index, $event.target.value)"
                        />
                    </td>
                    <td v-for="empty in (31-daysInMonth)" :key="'empty' + empty" class="cell"></td>
                    <td>{{ sum_meal_month(month_index) }}</td>
                </tr>
                <tr>
                    <td class="text-left">{{ $t('Km number')}}</td>
                    <td v-for="(day, day_index) in daysInMonth" :key="day_index" class="cell">
                        <input
                            type="number"
                            step="1"
                            min="0"
                            max="10000"
                            :value="get_km_record(month_index, day_index)"
                            @change="update_km_record(month_index, day_index, $event.target.value)"
                        />
                    </td>
                    <td v-for="empty in (31-daysInMonth)" :key="'empty' + empty" class="cell"></td>
                    <td>{{ sum_km_month(month_index) }}</td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
export default {
    props: ["year_init"],
    data() {
        return {
            year: this.year_init ? this.year_init : moment().year(),
            hours_prev: 20,
            holiday_hours_prev: 10,
            hours: 8.2, // working how many hours per day
            holidays: 25, // how many holidays per year
            hours_agreement: new Array(12), // array of hours for each month
            working_records: [],
            absence_records: [],
            meal_records: [],
            km_records: [],
            absences: [
                { id: 1, code: "VA", factor: 1, group_id: 1 },
                { id: 2, code: "VA2", factor: 0.5, group_id: 1 },
                { id: 3, code: "JF", factor: 1, group_id: 2 },
                { id: 4, code: "JF2", factor: 0.5, group_id: 2 },
                { id: 5, code: "M", factor: 1, group_id: 3 },
                { id: 6, code: "M8", factor: 0.8, group_id: 3 },
                { id: 7, code: "M6", factor: 0.6, group_id: 3 },
                { id: 8, code: "M5", factor: 0.5, group_id: 3 },
                { id: 9, code: "M4", factor: 0.4, group_id: 3 },
                { id: 10, code: "M2", factor: 0.25, group_id: 3 },
                { id: 11, code: "A", factor: 1, group_id: 4 },
                { id: 12, code: "A8", factor: 0.8, group_id: 4 },
                { id: 13, code: "A6", factor: 0.6, group_id: 4 },
                { id: 14, code: "A5", factor: 0.5, group_id: 4 },
                { id: 15, code: "A4", factor: 0.4, group_id: 4 },
                { id: 16, code: "A2", factor: 0.25, group_id: 4 },
                { id: 17, code: "DM", factor: 1, group_id: 4 },
                { id: 18, code: "MI", factor: 1, group_id: 4 },
                { id: 19, code: "MR", factor: 1, group_id: 4 },
                { id: 20, code: "NA", factor: 1, group_id: 4 },
                { id: 21, code: "DE", factor: 1, group_id: 4 }
            ], 
            testing: false
        };
    },
    computed: {
        factors() {
            let result = new Array();
            for (let i = 0; i < this.absences.length; i++) {
                result[this.absences[i].id] = this.absences[i].factor;
            }
            // console.log('factors', result);  // [empty, 1, 0.5, ...]
            return result;
        },
        holiday_array() {
            let result = this.absences
                .filter(elem => elem.group_id == 1)
                .map(el => el.id);
            // console.log(result);  // [1, 2]
            return result;
        }
    },
    created() {
        // moment.locale(this.$i18n.locale);
        this.fetch();
    },
    methods: {
        set_language(lang) {
            moment.locale(lang);
        },
        fetch() {
            // instead fetch from api, create two dimentional array
            // loop the outer array
            let empty_records = new Array(12);
            for (let i = 0; i < 12; i++) {
                // get the size of the inner array
                var innerArrayLength = this.get_days_in_month(this.year, i);
                empty_records[i] = new Array(innerArrayLength);
            }
            this.working_records = empty_records.slice();
            this.absence_records = empty_records.slice();
            this.meal_records = empty_records.slice();
            this.km_records = empty_records.slice();
        },
        print_decimal(num, n) {
            return num.toFixed(n);
        },
        round_decimal(num, n) {
            return Math.round(num * n) / n;
        },
        get_weekday(year, month, day) {
            return moment()
                .year(year)
                .month(month)
                .date(day)
                .format("ddd");
        },
        get_month(index) {
            return moment.months()[index];
        },
        get_days_in_month(year, month) {
            return moment()
                .year(year)
                .month(month)
                .daysInMonth();
        },
        // hours agreement
        update_hours_agreement(index, value) {
            this.$set(this.hours_agreement, index, Number(value));
        },
        sum_hours_agreement() {
            const reducer = (accumulator, currentValue) =>
                Number(accumulator) + Number(currentValue);
            let sum = this.hours_agreement.reduce(reducer, 0);
            if (sum > 0) return Math.round(sum * 100) / 100;
        },
        // working records
        get_working_record(month, day) {
            // have to see the data to decide function
            if (this.working_records[month]) {
                return this.working_records[month][day];
            }
        },
        update_working_record(month, day, value) {
            var record = this.working_records[month].slice();
            record[day] = Number(value);
            this.$set(this.working_records, month, record);
        },
        sum_working_month(month) {
            // have to see the data to decide function
            if (this.working_records[month]) {
                const reducer = (accumulator, currentValue) =>
                    Number(accumulator) + Number(currentValue);
                let sum = this.working_records[month].reduce(reducer, 0);
                if (sum > 0) return Math.round(sum * 100) / 100;
            }
        },
        // meal records
        get_meal_record(month, day) {
            if (this.meal_records[month]) {
                return this.meal_records[month][day];
            }
        },
        update_meal_record(month, day, value) {
            var record = this.meal_records[month].slice();
            record[day] = Number(value);
            this.$set(this.meal_records, month, record);
        },
        sum_meal_month(month) {
            if (this.meal_records[month]) {
                const reducer = (accumulator, currentValue) =>
                    Number(accumulator) + Number(currentValue);
                let sum = this.meal_records[month].reduce(reducer, 0);
                if (sum > 0) return sum;
            }
        },
        // km records
        get_km_record(month, day) {
            if (this.km_records[month]) {
                return this.km_records[month][day];
            }
        },
        update_km_record(month, day, value) {
            var record = this.km_records[month].slice();
            record[day] = Number(value);
            this.$set(this.km_records, month, record);
        },
        sum_km_month(month) {
            if (this.km_records[month]) {
                const reducer = (accumulator, currentValue) =>
                    Number(accumulator) + Number(currentValue);
                let sum = this.km_records[month].reduce(reducer, 0);
                if (sum > 0) return sum;
            }
        },
        // absence records
        get_absence_record(month, day) {
            // have to see the data to decide function
            if (this.absence_records[month]) {
                return this.absence_records[month][day];
            }
        },
        update_absence_record(month, day, value) {
            // console.log(month, day, value);
            var record = this.absence_records[month].slice();
            record[day] = parseInt(value); // id
            this.$set(this.absence_records, month, record);
        },
        sum_absence_holiday_month(month) {
            if (this.absence_records[month]) {
                let reducer = (accumulator, currentValue) =>
                    Number(accumulator) +
                    this.factors[currentValue] * this.hours;
                let sum = this.absence_records[month].reduce(reducer, 0);
                if (sum > 0) return Math.round(sum * 100) / 100;
            }
        },
        sum_absence_month(month) {
            if (this.absence_records[month]) {
                let reducer = (accumulator, currentValue) =>
                    Number(accumulator) +
                    Number(
                        this.holiday_array.includes(parseInt(currentValue))
                            ? 0
                            : this.factors[currentValue] * this.hours
                    );
                let sum = this.absence_records[month].reduce(reducer, 0);
                if (sum > 0) return Math.round(sum * 100) / 100;
            }
        },
        sum_holiday_month(month) {
            if (this.absence_records[month]) {
                let reducer = (accumulator, currentValue) =>
                    Number(accumulator) +
                    Number(
                        this.holiday_array.includes(parseInt(currentValue))
                            ? this.factors[currentValue] * this.hours
                            : 0
                    );
                let sum = this.absence_records[month].reduce(reducer, 0);
                if (sum > 0) return Math.round(sum * 100) / 100;
            }
        },
        sum_hours_month(month) {
            let hours_working = this.sum_working_month(month);
            let hours_absence = 0;
            if (this.absence_records[month]) {
                let reducer = (accumulator, currentValue) =>
                    Number(accumulator) +
                    this.factors[currentValue] * this.hours;
                hours_absence = this.absence_records[month].reduce(reducer, 0);
            }
            let sum = Number(hours_working) + Number(hours_absence);
            if (sum > 0) return Math.round(sum * 100) / 100;
        },
        balance_hours(month) {
            let balance =
                this.sum_hours_month(month) - this.hours_agreement[month];
            if (balance) return Math.round(balance * 100) / 100;
        },
        cum_working_hours_month(month) {
            let result = 0;
            for (let i = 0; i <= month; i++) {
                let hours = this.sum_working_month(i);
                if (hours > 0) result = Number(result) + Number(hours);
            }
            return result;
        },
        cum_absence_holiday_hours_month(month) {
            let result = 0;
            for (let i = 0; i <= month; i++) {
                let hours = this.sum_absence_holiday_month(i);
                if (hours > 0) result = Number(result) + Number(hours);
            }
            return result;
        },
        cum_absence_hours_month(month) {
            let result = 0;
            for (let i = 0; i <= month; i++) {
                let hours = this.sum_absence_month(i);
                if (hours > 0) result = Number(result) + Number(hours);
            }
            return result;
        },
        cum_holiday_hours_month(month) {
            let result = 0;
            for (let i = 0; i <= month; i++) {
                let hours = this.sum_holiday_month(i);
                if (hours > 0) result = Number(result) + Number(hours);
            }
            return result;
        },
        cum_hours_month(month) {
            let result =
                Number(this.hours_prev) +
                Number(this.cum_working_hours_month(month)) +
                Number(this.cum_absence_holiday_hours_month(month));
            return Math.round(result * 100) / 100;
        },
        cum_hours_agreement(month) {
            let result = 0;
            for (let i = 0; i <= month; i++) {
                if(this.hours_agreement[i]) result = Number(result) + Number(this.hours_agreement[i]);
            }
            if (result) return Math.round(result * 100) / 100;
        },
        cum_meals(month) {
            let result = 0;
            for (let i = 0; i <= month; i++) {
                let sum = this.sum_meal_month(i);
                if (sum > 0) result = Number(result) + Number(sum);
            }
            return result;
        },
        cum_km(month) {
            let result = 0;
            for (let i = 0; i <= month; i++) {
                let sum = this.sum_km_month(i);
                if (sum > 0) result = Number(result) + Number(sum);
            }
            return result;
        },
        balance_cum_hours_month(month) {
            let result =
                this.cum_hours_month(month) - this.cum_hours_agreement(month);
            if (result) return Math.round(result * 100) / 100;
        },
        balance_holiday_month(month) {
            let result =
                Number(this.holiday_hours_prev) +
                this.hours * this.holidays -
                Number(this.cum_holiday_hours_month(month));
            return Math.round(result * 100) / 100;
        },
        async test() {
            this.testing = true; 
            this.update_hours_agreement(0, 80);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_hours_agreement(1, 60);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_hours_agreement(2, 80);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            // january
            this.update_working_record(0, 0, 8);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_working_record(0, 1, 10);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_absence_record(0, 2, 1); // 'VA'  holiday
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_absence_record(0, 3, 3); // 'JF'  vacance
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_meal_record(0, 0, 1);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_meal_record(0, 1, 2);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_km_record(0, 0, 6);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_km_record(0, 1, 4);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec

            // febrary
            this.update_working_record(1, 0, 8);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_working_record(1, 1, 10);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_absence_record(1, 2, 1); // 'VA'  holiday
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_absence_record(1, 3, 3); // 'JF'  vacance
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_meal_record(1, 0, 1);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_meal_record(1, 1, 2);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_km_record(1, 0, 5);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 sec
            this.update_km_record(1, 1, 5);
            
            this.testing = false; 
        }
    }
};
</script>

<style>
</style>